import type { ReactNode } from 'react'

/**
 * Where you are: record → baseline → daily status.
 *
 * ── EXISTENCE, NEVER MAGNITUDE ──────────────────────────────────────────────
 *
 * Every state this renders was decided by its CALLER from the *existence* of a
 * fact — are there recordings, does a baseline have any sessions, did a result
 * load — and never from a value. Nothing here reads a score, a confidence or a
 * count, and the component takes none: it is handed three labelled states and
 * draws them. That is why it can live outside `pages/product/` without becoming
 * a second place where a measurement could be invented.
 *
 * ── "UNKNOWN" IS A STATE, AND IT IS NOT "TODO" ──────────────────────────────
 *
 * A read that failed, or pages nobody has looked at, is not the same as a step
 * that has not been started. Collapsing the two would let a network blip tell
 * someone their baseline does not exist. So `unknown` marks nothing and says
 * "Not known yet", which is the §4.5 distinction applied to a progress strip.
 */
export interface JourneyStep {
  /** The mono kicker, e.g. `01 · Record`. */
  label: string
  state: 'done' | 'todo' | 'unknown'
  title: string
  body?: ReactNode
}

/**
 * The rule, written without arithmetic.
 *
 * `here` is the first step still to do; if nothing is to do and all three are
 * done, it is the last one; otherwise no step is marked — because with an
 * `unknown` in the middle, "where you are" is a thing this component does not
 * know, and guessing it is exactly the false confidence the strip exists to
 * avoid.
 */
function hereIndex(steps: readonly JourneyStep[]): number | null {
  // `some` before `findIndex` rather than comparing the index to -1: this file
  // is scanned for numeric comparisons like every score-reading module, and a
  // sentinel test would cost an allowlist row saying nothing.
  if (steps.some((step) => step.state === 'todo')) {
    return steps.findIndex((step) => step.state === 'todo')
  }
  if (steps.every((step) => step.state === 'done')) return 2
  return null
}

export function JourneyStrip({ steps }: { steps: readonly [JourneyStep, JourneyStep, JourneyStep] }) {
  const here = hereIndex(steps)

  return (
    <ol className="journey" aria-label="Where you are">
      {steps.map((step, index) => {
        const isHere = index === here
        const classes = [
          'jstep',
          // Never both: a step you are ON is drawn as where you are, not as
          // finished, and a `--done --here` pair would say two things at once.
          step.state === 'done' && !isHere && 'jstep--done',
          isHere && 'jstep--here',
        ].filter(Boolean).join(' ')

        return (
          <li key={step.label} className={classes} aria-current={isHere ? 'step' : undefined}>
            <div className="jstep__num">
              <span>{step.label}</span>
              {/* Words, not only a tint (WCAG 1.4.1). "start here" on the first
                  step because there is nothing behind it to have come from. */}
              {isHere && (
                <span className="jstep__here">{index === 0 ? 'start here' : 'you are here'}</span>
              )}
            </div>
            <p className="jstep__title">{step.title}</p>
            {step.body !== undefined && <p className="jstep__state">{step.body}</p>}
          </li>
        )
      })}
    </ol>
  )
}
